import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { api } from "../../lib/api";

const BALANCE_LABEL = {
  available_balance: "Available Balance",
  held_balance: "Held Balance",
};

export default function WalletActionModal({ open, wallet, onClose, onDone }) {
  const [activeTab, setActiveTab] = useState("add");
  const [balanceType, setBalanceType] = useState("available_balance");
  const [amount, setAmount] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setActiveTab("add");
    setBalanceType("available_balance");
    setAmount("");
    setNotes("");
  }, [open, wallet]);

  if (!open || !wallet) return null;

  const currentBalance = Number(wallet[balanceType] || 0);
  const amountNum = Number(amount);
  const isRemove = activeTab === "remove";
  const exceedsBalance = isRemove && amountNum > currentBalance;

  const userName = wallet.user
    ? `${wallet.user.first_name || ""} ${wallet.user.last_name || ""}`.trim() || wallet.user.email || wallet.user.mobile_no
    : wallet.user_id;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!amount || Number.isNaN(amountNum) || amountNum <= 0) {
      return toast.error("Enter a valid amount greater than 0");
    }
    if (exceedsBalance) {
      return toast.error("Amount exceeds the selected balance");
    }

    setSaving(true);
    try {
      const endpoint = isRemove ? "remove" : "add";
      const res = await api.post(`/admin/wallet-action/${wallet.user_id}/${endpoint}`, {
        amount: amountNum,
        balance_type: balanceType,
        notes: notes || undefined,
      });
      toast.success(res?.data?.message || `${isRemove ? "Removed from" : "Added to"} wallet successfully`);
      onDone?.(res?.data?.data?.wallet);
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.message || err.message || "Action failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/30 grid place-items-center p-4">
      <div className="w-full max-w-md bg-white rounded-2xl border p-5">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-lg font-bold">Wallet Action</div>
            <div className="text-xs text-gray-500">{userName} • {wallet.user_id}</div>
          </div>
          <button onClick={onClose} className="text-sm px-3 py-1 rounded-lg hover:bg-gray-100">
            Close
          </button>
        </div>

        <div className="mt-4 flex gap-2">
          <button
            type="button"
            onClick={() => setActiveTab("add")}
            className={`flex-1 rounded-xl px-3 py-2 text-sm font-semibold ${
              activeTab === "add" ? "bg-black text-white" : "bg-white border text-gray-700"
            }`}
          >
            Add Wallet Money
          </button>
          <button
            type="button"
            onClick={() => setActiveTab("remove")}
            className={`flex-1 rounded-xl px-3 py-2 text-sm font-semibold ${
              activeTab === "remove" ? "bg-black text-white" : "bg-white border text-gray-700"
            }`}
          >
            Remove Wallet Money
          </button>
        </div>

        <form onSubmit={handleSubmit} className="mt-4 space-y-3">
          <div>
            <label className="text-sm font-semibold">Balance Type</label>
            <select
              className="mt-2 w-full border rounded-xl px-3 py-2"
              value={balanceType}
              onChange={(e) => setBalanceType(e.target.value)}
            >
              <option value="available_balance">
                {BALANCE_LABEL.available_balance} (₹{Number(wallet.available_balance || 0).toLocaleString("en-IN")})
              </option>
              <option value="held_balance">
                {BALANCE_LABEL.held_balance} (₹{Number(wallet.held_balance || 0).toLocaleString("en-IN")})
              </option>
            </select>
          </div>

          <div>
            <label className="text-sm font-semibold">Enter Amount</label>
            <input
              type="number"
              min={0.01}
              step="0.01"
              className="mt-2 w-full border rounded-xl px-3 py-2"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
            />
            {isRemove ? (
              <p className={`text-xs mt-1 ${exceedsBalance ? "text-red-600" : "text-gray-500"}`}>
                Current {BALANCE_LABEL[balanceType]}: ₹{currentBalance.toLocaleString("en-IN")}
              </p>
            ) : null}
          </div>

          <div>
            <label className="text-sm font-semibold">Notes (optional)</label>
            <textarea
              className="mt-2 w-full border rounded-xl px-3 py-2 min-h-[70px]"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="rounded-xl border px-4 py-2 text-sm font-semibold hover:bg-gray-50">
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving || exceedsBalance}
              className="rounded-xl bg-black px-4 py-2 text-white text-sm font-semibold disabled:opacity-60"
            >
              {saving ? "Saving..." : isRemove ? "Remove Money" : "Add Money"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
